/**
 * Approvals — a manager's side of a submitted week (T&B-1 PR 3).
 *
 *   Waiting  submitted time and disbursements by person and week; approve
 *            (the server stamps the rate in force on the work date and the
 *            value, decision 2) or return to the author with a reason. A
 *            manager's own time can be approved too: it is flagged
 *            self-approved and goes to the other manager's monthly sign-off
 *            (decision 3, Self-approved time).
 *   Months   close a month once everything dated in it is approved; nothing
 *            in a closed month moves until a manager reopens it, with a
 *            reason that is kept.
 *
 * Managers only, and signed in: the database records who approved, returned,
 * closed and reopened, and refuses anyone else. The page decides nothing --
 * every refusal is the server's sentence, shown as it comes back.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type AuthState, type MonthRow, type ObligationTypeInUse, type QueueWeek } from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState, LoadingState } from '../components/States'
import { clientLabel, inputCls, primaryBtn, quietBtn, rand } from '../components/TimeCapture'

type Tab = 'waiting' | 'months'
const sectionCls = 'mb-4 rounded-eq-md border border-eq-border bg-white px-5 py-4'
const thCls = 'px-2 py-1 text-left font-semibold'
const tdCls = 'px-2 py-1.5 align-top'
type Act = (fn: () => Promise<unknown>, done: string) => Promise<boolean>

const dayHead = (d: string) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })
const whenLabel = (iso: string) =>
  new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

export function ApprovalsPage({ obligationTypes, auth }: { obligationTypes: ObligationTypeInUse[]; auth: AuthState }) {
  const [tab, setTab] = useState<Tab>('waiting')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [version, setVersion] = useState(0)

  const act: Act = async (fn, done) => {
    if (busy) return false
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await fn()
      setNotice(done)
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setVersion((v) => v + 1)
      setBusy(false)
    }
  }

  const signedInManager = auth.mode === 'cognito' && auth.user?.role === 'manager'

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Time"
      title="Approvals"
      subtitle="Submitted time and disbursements, waiting for a manager. Approving stamps the rate and value; returning sends it back with your reason."
    >
      {auth.mode === 'anonymous' ? (
        <ErrorState message="Approving time records who approved it, so it needs sign-in. This server is running without Cognito (local development)." />
      ) : !signedInManager ? (
        <ErrorState message="Only a manager may approve time." />
      ) : (
        <>
          <div className="mb-4 flex gap-1 border-b border-eq-border" role="tablist">
            {(
              [
                ['waiting', 'Waiting for approval'],
                ['months', 'Months'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                onClick={() => {
                  setTab(id)
                  setError(null)
                  setNotice(null)
                }}
                className={[
                  '-mb-px border-b-2 px-3 py-2 font-heading text-[13px] font-bold',
                  tab === id ? 'border-eq-navy text-eq-navy' : 'border-transparent text-eq-neutral hover:text-eq-navy',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>
          {error && (
            <div className="mb-4">
              <ErrorState message={error} />
            </div>
          )}
          {notice && <div className="mb-4 rounded-eq-md border border-eq-border bg-white px-4 py-2 text-[13.5px]">{notice}</div>}
          {tab === 'waiting' ? <Waiting act={act} busy={busy} version={version} /> : <Months act={act} busy={busy} version={version} />}
        </>
      )}
    </AppShell>
  )
}

function useLoad<T>(load: () => Promise<T>, version: number) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const run = useCallback(load, [load])
  useEffect(() => {
    let live = true
    run().then(
      (d) => live && (setData(d), setError(null)),
      (e: unknown) => live && setError(e instanceof Error ? e.message : String(e))
    )
    return () => {
      live = false
    }
  }, [run, version])
  return { data, error }
}

// ---------------------------------------------------------------------------
// Waiting for approval
// ---------------------------------------------------------------------------

function Waiting({ act, busy, version }: { act: Act; busy: boolean; version: number }) {
  const { data, error } = useLoad(api.approvals, version)
  if (error) return <ErrorState message={error} />
  if (data === null) return <LoadingState what="submitted time" />
  if (data.weeks.length === 0) {
    return <p className="text-[14px] text-eq-neutral">Nothing is waiting. Submitted weeks appear here.</p>
  }
  return (
    <>
      {data.weeks.map((w) => (
        <WeekCard key={`${w.person.id}|${w.weekStart}`} week={w} act={act} busy={busy} />
      ))}
    </>
  )
}

function WeekCard({ week, act, busy }: { week: QueueWeek; act: Act; busy: boolean }) {
  const all = [...week.entries.map((e) => `e:${e.id}`), ...week.disbursements.map((d) => `d:${d.id}`)]
  const key = all.join(',')
  const [picked, setPicked] = useState<Set<string>>(() => new Set(all))
  const [reason, setReason] = useState('')
  // After a partial approve or return the card stays, with fewer items: start again from all of them.
  useEffect(() => {
    setPicked(new Set(key === '' ? [] : key.split(',')))
    setReason('')
  }, [key])
  const ids = () => ({
    entryIds: [...picked].filter((k) => k.startsWith('e:')).map((k) => k.slice(2)),
    disbursementIds: [...picked].filter((k) => k.startsWith('d:')).map((k) => k.slice(2)),
  })
  const toggle = (k: string) =>
    setPicked((p) => {
      const n = new Set(p)
      if (n.has(k)) n.delete(k)
      else n.add(k)
      return n
    })
  const count = picked.size
  const disbTotal = week.disbursements.reduce((a, d) => a + d.amountCents, 0)

  return (
    <section className={sectionCls}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 font-heading text-[15px] font-bold text-eq-navy">
          {week.person.name} <span className="font-normal text-eq-neutral">· week of {dayHead(week.weekStart)}</span>
        </h2>
        <span className="text-[13px] tabular-nums text-eq-neutral">
          {week.hours} h · {rand(week.wouldValueCents)}
          {disbTotal > 0 && <> · disbursements {rand(disbTotal)}</>}
        </span>
      </div>
      {week.ownTime && (
        <p className="mb-2 mt-0 rounded-eq-sm bg-eq-warning-bg px-3 py-1.5 text-[12.5px] text-eq-warning">
          This is your own time. You may approve it; it is flagged self-approved and another manager signs it off monthly under Self-approved time.
        </p>
      )}
      {week.rateMissing > 0 && (
        <p className="mb-2 mt-0 rounded-eq-sm bg-eq-danger-bg px-3 py-1.5 text-[12.5px] text-eq-danger">
          {week.rateMissing} {week.rateMissing === 1 ? 'entry has' : 'entries have'} no rate in force for {week.person.name} on the work date, so{' '}
          {week.rateMissing === 1 ? 'it' : 'they'} cannot be valued or approved. Record the rate under{' '}
          <Link to="/time/jobs-and-prices" className="underline">
            Jobs and prices → Rates and hours
          </Link>
          .
        </p>
      )}
      <table className="w-full border-collapse text-[13.5px]">
        <thead className="text-[12px] text-eq-neutral">
          <tr>
            <th className={thCls}>
              <input
                type="checkbox"
                aria-label="Select all"
                checked={count === all.length}
                onChange={() => setPicked(count === all.length ? new Set() : new Set(all))}
              />
            </th>
            <th className={thCls}>Day</th>
            <th className={thCls}>Hours</th>
            <th className={thCls}>Billing job</th>
            <th className={thCls}>Client</th>
            <th className={thCls}>Narrative</th>
            <th className={`${thCls} text-right`}>Value</th>
          </tr>
        </thead>
        <tbody>
          {week.entries.map((e) => (
            <tr key={e.id} className="border-t border-eq-border">
              <td className={tdCls}>
                <input type="checkbox" aria-label="Select" checked={picked.has(`e:${e.id}`)} onChange={() => toggle(`e:${e.id}`)} />
              </td>
              <td className={`${tdCls} whitespace-nowrap`}>{dayHead(e.workDate)}</td>
              <td className={`${tdCls} tabular-nums`}>
                {e.hours}
                {e.source === 'timer' && <div className="text-[11px] text-eq-helper">timer</div>}
              </td>
              <td className={tdCls}>
                {e.job.name}
                {e.task && <span className="text-eq-helper"> · {e.task.name}</span>}
                {e.job.kind === 'leave' && <span className="ml-1 rounded-eq-pill bg-eq-ice px-2 py-0.5 text-[11px]">leave</span>}
                {e.job.kind === 'work' && !e.job.chargeable && <div className="text-[11px] text-eq-helper">not chargeable</div>}
              </td>
              <td className={tdCls}>{e.client ? clientLabel(e.client) : '—'}</td>
              <td className={`${tdCls} max-w-[22rem]`}>{e.narrative ?? ''}</td>
              <td className={`${tdCls} whitespace-nowrap text-right tabular-nums`}>
                {e.wouldValueCents === null ? <span className="text-eq-danger">no rate</span> : rand(e.wouldValueCents)}
                {e.chargeRateCents !== null && <div className="text-[11px] text-eq-helper">at {rand(e.chargeRateCents)}/h</div>}
              </td>
            </tr>
          ))}
          {week.disbursements.map((d) => (
            <tr key={d.id} className="border-t border-eq-border">
              <td className={tdCls}>
                <input type="checkbox" aria-label="Select" checked={picked.has(`d:${d.id}`)} onChange={() => toggle(`d:${d.id}`)} />
              </td>
              <td className={`${tdCls} whitespace-nowrap`}>{dayHead(d.incurredDate)}</td>
              <td className={tdCls}>
                <span className="rounded-eq-pill bg-eq-neutral-bg px-2 py-0.5 text-[11px]">disbursement</span>
              </td>
              <td className={tdCls}>{d.description}</td>
              <td className={tdCls}>{clientLabel(d.client)}</td>
              <td className={tdCls}></td>
              <td className={`${tdCls} text-right tabular-nums`}>{rand(d.amountCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          className={primaryBtn(busy || count === 0)}
          disabled={busy || count === 0}
          onClick={() => void act(() => api.approve(ids()), `Approved ${count} ${count === 1 ? 'item' : 'items'} for ${week.person.name}.`)}
        >
          Approve {count === all.length ? 'week' : `${count} selected`}
        </button>
        <span className="flex items-center gap-2">
          <input
            className={`${inputCls} w-72`}
            placeholder="Reason for returning (the author sees it)"
            value={reason}
            maxLength={2000}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            className={quietBtn}
            disabled={busy || count === 0 || reason.trim() === ''}
            title={reason.trim() === '' ? 'Say why first' : undefined}
            onClick={() =>
              void act(() => api.returnToAuthor({ ...ids(), reason }), `Returned ${count} ${count === 1 ? 'item' : 'items'} to ${week.person.name}.`)
            }
          >
            Return {count === all.length ? 'week' : `${count} selected`}
          </button>
        </span>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Months
// ---------------------------------------------------------------------------

function Months({ act, busy, version }: { act: Act; busy: boolean; version: number }) {
  const { data, error } = useLoad(api.timeMonths, version)
  if (error) return <ErrorState message={error} />
  if (data === null) return <LoadingState what="months" />
  return (
    <section className={sectionCls}>
      <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
        Close a month once everything dated in it is approved. Nothing dated in a closed month can then be added, changed, approved or returned.
        Reopening is kept with its reason.
      </p>
      <table className="w-full border-collapse text-[13.5px]">
        <thead className="text-[12px] text-eq-neutral">
          <tr>
            <th className={thCls}>Month</th>
            <th className={`${thCls} text-right`}>Drafts</th>
            <th className={`${thCls} text-right`}>Submitted</th>
            <th className={`${thCls} text-right`}>Returned</th>
            <th className={`${thCls} text-right`}>Approved</th>
            <th className={thCls}>State</th>
            <th className={thCls}></th>
          </tr>
        </thead>
        <tbody>
          {data.months.map((m: MonthRow) => (
            <tr key={m.month} className="border-t border-eq-border">
              <td className={`${tdCls} whitespace-nowrap font-semibold`}>{m.label}</td>
              <td className={`${tdCls} text-right tabular-nums`}>{m.counts.draft || ''}</td>
              <td className={`${tdCls} text-right tabular-nums`}>{m.counts.submitted || ''}</td>
              <td className={`${tdCls} text-right tabular-nums`}>{m.counts.returned || ''}</td>
              <td className={`${tdCls} text-right tabular-nums`}>{m.counts.approved || ''}</td>
              <td className={tdCls}>
                {m.closed ? 'Closed' : m.ended ? 'Open' : 'Current'}
                {m.last && (
                  <div className="text-[11.5px] text-eq-helper">
                    {m.last.action === 'closed' ? 'Closed' : 'Reopened'} by {m.last.by}, {whenLabel(m.last.at)}
                    {m.last.reason && <> — {m.last.reason}</>}
                  </div>
                )}
              </td>
              <td className={`${tdCls} whitespace-nowrap text-right`}>
                {m.closed ? (
                  <button
                    className={quietBtn}
                    disabled={busy}
                    onClick={() => {
                      const reason = window.prompt(`Why reopen ${m.label}? The reason is kept.`)
                      if (reason !== null) void act(() => api.reopenMonth(m.month, reason), `${m.label} reopened.`)
                    }}
                  >
                    Reopen…
                  </button>
                ) : m.ended ? (
                  <button
                    className={primaryBtn(busy || !m.canClose)}
                    disabled={busy || !m.canClose}
                    title={m.canClose ? undefined : 'Everything dated in the month must be approved first'}
                    onClick={() => {
                      if (window.confirm(`Close ${m.label}? Nothing dated in it can then change until a manager reopens it.`)) {
                        void act(() => api.closeMonth(m.month), `${m.label} closed.`)
                      }
                    }}
                  >
                    Close
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
